import {
  AutocompleteGenericEntity,
  FetchAutocomplete,
  FetchAutocompleteProps,
} from '@australiangreens/ag-internal-components';
import { Skeleton } from '@mui/material';
import { useMemo, useState } from 'react';

import { usePathwaySteps } from '@hooks/usePathways';
import { FieldMetaData } from './types';

type PathwayStepsTFCProps = Omit<
  FetchAutocompleteProps<AutocompleteGenericEntity>,
  'value' | 'onChange' | 'preLoadedOptions'
> & {
  onChange: (value: number[], fieldMetaData: FieldMetaData) => void;
  /** IDs for Restart pre-fill (matched to preLoadedOptions when they load) */
  initialValue?: number[];
};

/**
 * the pathwaysSteps field works slightly differently than the other fetch
 * autocomplete ones, in that it fetches all values from the backend and uses
 * that as the preloaded options - rather than either fetching live as user
 * types or using hardcoded options.
 */
export default function PathwayStepsTFC({
  onChange,
  initialValue,
  ...props
}: PathwayStepsTFCProps) {
  const [internalValue, setInternalValue] = useState<AutocompleteGenericEntity[]>([]);
  const [touched, setTouched] = useState(false);
  const { data: pathwaySteps } = usePathwaySteps();

  const value = useMemo(() => {
    if (!touched && internalValue.length === 0 && initialValue?.length && pathwaySteps?.length) {
      return pathwaySteps.filter((p) =>
        initialValue.includes(typeof p.id === 'number' ? p.id : parseInt(String(p.id)))
      );
    }
    return internalValue;
  }, [touched, internalValue, initialValue, pathwaySteps]);

  const handleOnChange = (newValue: AutocompleteGenericEntity[]) => {
    setTouched(true);
    setInternalValue(newValue);
    const state = newValue.length === 0 ? 'empty' : 'valid';

    onChange(
      newValue.map((x) => Number(x.id)),
      { state, error: null }
    );
  };

  return pathwaySteps ? (
    <FetchAutocomplete
      onChange={handleOnChange}
      value={value}
      preLoadedOptions={pathwaySteps}
      minLength={0}
      textFieldColor="secondary"
      {...props}
    />
  ) : (
    <Skeleton />
  );
}
